// Bound the whole operation, including reading the body. The race also settles
// if a provider ignores AbortSignal; callers can always leave/retry the page.
export function runWithDeadline(operation, { signal, timeoutMs = 20000 } = {}) {
    const controller = new AbortController();
    return new Promise((resolve, reject) => {
        let finished = false;
        let timer;
        const finish = (callback, value) => {
            if (finished) return;
            finished = true;
            clearTimeout(timer);
            signal?.removeEventListener('abort', abort);
            callback(value);
        };
        const abort = () => {
            controller.abort();
            finish(reject, Object.assign(new Error('已取消載入。'), { name: 'AbortError' }));
        };
        if (signal?.aborted) { abort(); return; }
        signal?.addEventListener('abort', abort, { once: true });
        timer = setTimeout(() => {
            controller.abort();
            finish(reject, Object.assign(new Error('載入逾時，請稍後重試。'), { name: 'TimeoutError' }));
        }, timeoutMs);
        Promise.resolve().then(() => operation(controller.signal))
            .then(value => finish(resolve, value), error => finish(reject, error));
    });
}

export function requestJson(url, options = {}, limits = {}) {
    return runWithDeadline(async signal => {
        const response = await fetch(url, { ...options, signal });
        let payload;
        try { payload = JSON.parse(await response.text()); }
        catch { throw new Error(`服務回傳無法解析的內容（HTTP ${response.status}）。`); }
        if (!response.ok) throw new Error(`服務請求失敗（HTTP ${response.status}）。`);
        return payload;
    }, limits);
}

export async function readLimitedBytes(response, maxBytes) {
    if (Number(response.headers?.get('content-length')) > maxBytes) throw new Error('檔案超過大小上限。');
    if (!response.body?.getReader) {
        const buffer = await response.arrayBuffer();
        if (buffer.byteLength > maxBytes) throw new Error('檔案超過大小上限。');
        return buffer;
    }
    const reader = response.body.getReader();
    const chunks = [];
    let length = 0;
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            length += value.byteLength;
            if (length > maxBytes) throw new Error('檔案超過大小上限。');
            chunks.push(value);
        }
    } catch (error) {
        await reader.cancel().catch(() => {});
        throw error;
    } finally { reader.releaseLock(); }
    const bytes = new Uint8Array(length);
    let offset = 0;
    chunks.forEach(chunk => { bytes.set(chunk, offset); offset += chunk.byteLength; });
    return bytes.buffer;
}
